import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'

export interface AdminUser {
  id: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  groups: string[]
  last_login?: string
  date_joined: string
  last_recovery_at?: string | null
  is_simulation?: boolean
}

export interface SendRecoveryResult {
  action: string
  sent_to: string
  cooldown_until: string
}

export interface CreateUserData {
  email: string
  first_name?: string
  last_name?: string
  password: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  group_names?: string[]
}

export interface ExperienceData {
  title: string
  company: string
  start_date: string
  end_date?: string
  description?: string
  skills?: string[]
}

export interface UpdateUserData {
  email?: string
  first_name?: string
  last_name?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  group_names?: string[]
  bio?: string
  city?: string
  country?: string
  add_skills?: string[]
  remove_skills?: string[]
  experiences?: ExperienceData[]
}

export interface AssignGroupsData {
  group_names: string[]
  assign_mode: 'replace' | 'add' | 'remove'
}

export interface UsersQueryParams {
  search?: string
  group_filter?: string
  limit?: number
  include_simulated?: boolean
}

export function useUsers(params?: UsersQueryParams) {
  return useQuery({
    queryKey: ['admin-users', params],
    queryFn: async (): Promise<AdminUser[]> => {
      const searchParams = new URLSearchParams()
      if (params?.search) searchParams.append('search', params.search)
      if (params?.group_filter) searchParams.append('group_filter', params.group_filter)
      if (params?.limit) searchParams.append('limit', params.limit.toString())
      if (params?.include_simulated) searchParams.append('include_simulated', 'true')

      const url = `/api/accounts/users?${searchParams.toString()}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }

      const data = await response.json()
      return data || []
    },
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: ['admin-user', userId],
    queryFn: async (): Promise<AdminUser> => {
      const response = await authorizedFetch(`/api/accounts/users/${userId}`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found')
        }
        throw new Error('Failed to fetch user')
      }

      const data = await response.json()
      return data
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateUserData): Promise<AdminUser> => {
      const response = await authorizedFetch('/api/accounts/users', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create user')
      }

      const result = await response.json()
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserData }): Promise<AdminUser> => {
      const response = await authorizedFetch(`/api/accounts/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        const detail = Array.isArray(error.detail)
          ? error.detail.map((item: { msg?: string } | string) =>
              typeof item === 'string' ? item : item?.msg || 'Invalid request',
            ).join('; ')
          : error.detail
        throw new Error(error.error || detail || 'Failed to update user')
      }

      const result = await response.json()
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', variables.userId] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      const response = await authorizedFetch(`/api/accounts/users/${userId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete user')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useAssignGroups() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: AssignGroupsData }): Promise<AdminUser> => {
      const response = await authorizedFetch(`/api/accounts/users/${userId}/groups`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to assign groups')
      }

      const result = await response.json()
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', variables.userId] })
    },
  })
}

export function useSendUserRecovery() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string): Promise<SendRecoveryResult> => {
      const response = await authorizedFetch(`/api/accounts/users/${userId}/send-recovery`, {
        method: 'POST',
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Failed to send the email')
      }

      return response.json()
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] })
    },
  })
}

export interface GenerateSamplesData {
  count: number
  career_types?: string[]
  skill_hints?: string[]
  industry_hints?: string[]
}

export function useGenerateUserSamples() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: GenerateSamplesData): Promise<void> => {
      const response = await authorizedFetch('/api/accounts/users/generate-samples', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to generate sample users')
      }

      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

